import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Globe from 'react-globe.gl';
import { MeshBasicMaterial } from 'three';
import { Box, Skeleton, Typography, useTokens } from '@barracuda-internal/bds-core';
import { useThemeMode } from 'common/ThemeMode';

/**
 * 3D globe with country-level choropleth.
 *
 * Built on `react-globe.gl` (three.js). Countries are real GeoJSON
 * polygons coloured by aggregated traffic share — darker = more
 * traffic. The sphere itself is hidden (`showGlobe={false}`) so the
 * "ocean" is the page background (true transparency). Country
 * polygons hover in space, held together visually by the atmosphere
 * glow.
 *
 * Props:
 *   items: [{ region, count, percentage? }]
 *   size:  px (square). Default 320.
 *
 * Behaviour:
 *   - Auto-rotates slowly when idle.
 *   - Click-and-drag rotates manually.
 *   - Hover a country → BDS tooltip lists every region that
 *     contributed, with per-region counts. Auto-rotation pauses.
 *
 * Aggregation:
 *   AWS regions live in real countries (us-east-1 + us-west-2 → US).
 *   Aggregation is done client-side in `useMemo` — no backend change
 *   required.
 */

// Served from our own origin (public/data/) — frozen snapshot of
// Natural Earth's 110m admin boundaries, ~477 KB. No third-party CDN
// dependency, no SRI to manage, cacheable behind our own headers.
const COUNTRIES_GEOJSON_URL = '/data/ne_110m_admin_0_countries.geojson';

const REGION_TO_COUNTRY = {
  'us-east-1': 'US',
  'us-east-2': 'US',
  'us-west-1': 'US',
  'us-west-2': 'US',
  'ca-central-1': 'CA',
  'eu-west-1': 'IE',
  'eu-west-2': 'GB',
  'eu-west-3': 'FR',
  'eu-central-1': 'DE',
  'eu-north-1': 'SE',
  'ap-south-1': 'IN',
  'ap-northeast-1': 'JP',
  'ap-northeast-2': 'KR',
  'ap-southeast-1': 'SG',
  'ap-southeast-2': 'AU',
  'sa-east-1': 'BR',
  'us-east': 'US',
  'us-west': 'US',
};

const COUNTRY_DISPLAY = {
  US: 'United States',
  CA: 'Canada',
  IE: 'Ireland',
  GB: 'United Kingdom',
  FR: 'France',
  DE: 'Germany',
  SE: 'Sweden',
  IN: 'India',
  JP: 'Japan',
  KR: 'South Korea',
  SG: 'Singapore',
  AU: 'Australia',
  BR: 'Brazil',
};

// Lazy-load the GeoJSON once for the whole module.
let countriesPromise = null;
const loadCountries = () => {
  if (countriesPromise) return countriesPromise;
  countriesPromise = fetch(COUNTRIES_GEOJSON_URL)
    .then((res) => res.json())
    .then((data) => data.features || [])
    .catch(() => []);
  return countriesPromise;
};

const RegionsGlobe = ({ items, size = 400 }) => {
  const globeRef = useRef(null);
  const containerRef = useRef(null);
  const tokens = useTokens();
  const { mode } = useThemeMode();
  const [countries, setCountries] = useState(null);
  const [hovered, setHovered] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isTabVisible, setIsTabVisible] = useState(
    typeof document === 'undefined' ? true : !document.hidden
  );
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    let alive = true;
    loadCountries().then((features) => {
      if (alive) setCountries(features);
    });
    return () => {
      alive = false;
    };
  }, []);

  // Track on-screen visibility — globe doesn't render or rotate when off-screen.
  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') {
      setIsVisible(true);
      return undefined;
    }
    const node = containerRef.current;
    if (!node) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => setIsVisible(entry.isIntersecting),
      { threshold: 0.05 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  // Pause rotation when the browser tab is hidden.
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const handler = () => setIsTabVisible(!document.hidden);
    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  }, []);

  // Respect users with motion-sensitivity preference.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mq.matches);
    const handler = (e) => setPrefersReducedMotion(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const countriesByIso = useMemo(() => {
    const out = {};
    let total = 0;
    (items || []).forEach((item) => {
      const iso = REGION_TO_COUNTRY[item.region];
      if (!iso) return;
      total += item.count;
      if (!out[iso]) {
        out[iso] = {
          iso,
          name: COUNTRY_DISPLAY[iso] || iso,
          totalCount: 0,
          regions: [],
        };
      }
      out[iso].totalCount += item.count;
      out[iso].regions.push({
        region: item.region,
        count: item.count,
        percentage: item.percentage,
      });
    });
    Object.values(out).forEach((c) => {
      c.regions.sort((a, b) => b.count - a.count);
      c.percentage = total
        ? Math.round((c.totalCount / total) * 1000) / 10
        : 0;
    });
    return out;
  }, [items]);

  const maxCountryShare = useMemo(
    () =>
      Math.max(
        ...Object.values(countriesByIso).map((c) => c.percentage),
        1
      ),
    [countriesByIso]
  );

  // Tinted, translucent globe material — the "ocean". Saturated brand
  // lavender at moderate opacity so the colour actually reads instead
  // of washing out to white. depthWrite: false stops the sphere from
  // fighting the country polygons for z-order.
  const globeMaterial = useMemo(
    () =>
      new MeshBasicMaterial({
        color: mode === 'dark' ? 0x4a3580 : 0xb8a5e6,
        transparent: true,
        opacity: mode === 'dark' ? 0.55 : 0.32,
        depthWrite: false,
      }),
    [mode]
  );

  // three.js retains GPU buffers/shader programs until disposed.
  useEffect(() => () => globeMaterial.dispose(), [globeMaterial]);

  // Auto-rotate, gated on hover / on-screen / tab-visible / motion preference.
  // Off-screen or background tabs would otherwise burn CPU+GPU forever.
  useEffect(() => {
    if (!globeRef.current || !countries) return;
    const controls = globeRef.current.controls();
    if (!controls) return;
    controls.autoRotate =
      !hovered && isVisible && isTabVisible && !prefersReducedMotion;
    controls.autoRotateSpeed = 3.5;
    controls.enableZoom = false;
  }, [countries, hovered, isVisible, isTabVisible, prefersReducedMotion]);

  // Pull the camera back enough to see the whole globe.
  useEffect(() => {
    if (!globeRef.current || !countries) return;
    globeRef.current.pointOfView({ lat: 20, lng: 10, altitude: 2.4 }, 0);
  }, [countries]);

  const getIso = useCallback(
    (feat) =>
      feat?.properties?.ISO_A2
      || feat?.properties?.ISO_A2_EH
      || feat?.properties?.iso_a2
      || null,
    []
  );

  // Choropleth. No-data countries render solid white (or near-white in
  // dark mode) so they read as plain land masses against the tinted
  // ocean. Countries with traffic get a purple wash whose alpha tracks
  // their share of the total.
  const polygonCapColor = useCallback(
    (feat) => {
      const iso = getIso(feat);
      const country = iso && countriesByIso[iso];
      if (!country) {
        // White but translucent — lets the lavender ocean wash through
        // so the landmass feels integrated with the globe.
        return mode === 'dark'
          ? 'rgba(220, 220, 232, 0.30)'
          : 'rgba(255, 255, 255, 0.38)';
      }
      const intensity = 0.45 + (country.percentage / maxCountryShare) * 0.5;
      return `rgba(99, 46, 182, ${intensity})`;
    },
    [countriesByIso, getIso, maxCountryShare, mode]
  );

  // Polygon sides — only visible on raised (active) countries. No-data
  // countries sit flat on the sphere so adjacent landmasses fuse into
  // continuous shapes with no visible internal borders.
  const polygonSideColor = useCallback(
    (feat) => {
      const iso = getIso(feat);
      return iso && countriesByIso[iso]
        ? 'rgba(99, 46, 182, 0.22)'
        : 'rgba(0, 0, 0, 0)';
    },
    [countriesByIso, getIso]
  );

  // No country outlines anywhere — adjacent same-coloured polygons
  // (e.g. all of Europe's no-data countries) blend into a continuous
  // landmass with no internal divisions.
  const polygonStrokeColor = useCallback(() => 'rgba(0, 0, 0, 0)', []);

  // No-data countries are flat (no sides → no visible neighbour
  // boundary). Active countries are slightly raised so they pop and
  // the choropleth depth reads.
  const polygonAltitude = useCallback(
    (feat) => {
      const iso = getIso(feat);
      return iso && countriesByIso[iso] ? 0.012 : 0.003;
    },
    [countriesByIso, getIso]
  );

  const handlePolygonHover = useCallback(
    (feat) => {
      const iso = getIso(feat);
      const country = iso && countriesByIso[iso];
      setHovered(country || null);
    },
    [countriesByIso, getIso]
  );

  if (!countries) {
    return (
      <Box ref={containerRef} sx={{ width: size, height: size, mx: 'auto' }}>
        <Skeleton variant="circular" width={size} height={size} />
      </Box>
    );
  }

  return (
    <Box
      ref={containerRef}
      sx={{
        position: 'relative',
        width: size,
        height: size,
        mx: 'auto',
      }}
    >
      <Globe
        ref={globeRef}
        width={size}
        height={size}
        backgroundColor="rgba(0,0,0,0)"
        // Sphere is back, but with a translucent lavender material so
        // the "ocean" reads as a soft brand wash instead of a dark
        // ball. Page background still shows through where the material
        // is most transparent.
        showGlobe
        globeMaterial={globeMaterial}
        showAtmosphere
        atmosphereColor={
          mode === 'dark' ? 'rgb(120, 80, 200)' : 'rgb(160, 130, 230)'
        }
        atmosphereAltitude={0.15}
        polygonsData={countries}
        polygonCapColor={polygonCapColor}
        polygonSideColor={polygonSideColor}
        polygonStrokeColor={polygonStrokeColor}
        polygonAltitude={polygonAltitude}
        polygonsTransitionDuration={300}
        onPolygonHover={handlePolygonHover}
      />

      {hovered && (
        <Box
          sx={{
            position: 'absolute',
            top: tokens.aliasesSpaceComponentDense,
            right: tokens.aliasesSpaceComponentDense,
            backgroundColor: tokens.aliasesColorBgStaticContainerEmphasis,
            paddingX: tokens.aliasesSpaceContainerDense,
            paddingY: tokens.aliasesSpaceComponentDense,
            borderRadius: tokens.aliasesBorderRadiusDefault,
            boxShadow: '0 6px 20px rgba(0, 0, 0, 0.22)',
            pointerEvents: 'none',
            minWidth: 180,
            maxWidth: 240,
            zIndex: 4,
          }}
        >
          <Typography
            variant="body1Bold"
            sx={{ color: tokens.aliasesColorTextInverseEmphasis }}
          >
            {hovered.name}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: tokens.aliasesColorTextInverseSubtle,
              display: 'block',
              marginBottom: tokens.aliasesSpaceComponentDense,
            }}
          >
            {hovered.totalCount.toLocaleString()} scans · {hovered.percentage}%
          </Typography>
          {hovered.regions.map((r) => (
            <Box
              key={r.region}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: tokens.aliasesSpaceComponentDense,
              }}
            >
              <Typography
                variant="micro"
                sx={{ color: tokens.aliasesColorTextInverseSubtle }}
              >
                {r.region}
              </Typography>
              <Typography
                variant="micro"
                sx={{
                  color: tokens.aliasesColorTextInverseEmphasis,
                  fontWeight: 600,
                }}
              >
                {r.count.toLocaleString()}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default RegionsGlobe;
